/**
 * "What we do" — the programme areas the NGO delivers against, each an image-led card
 * with a lucide icon, short copy and an outcome line. Content-rich, scannable, and the
 * categories map 1:1 to the case taxonomy on the Served wall.
 */
import { Home, Wheat, HeartPulse, GraduationCap, Droplets, LifeBuoy, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useReveal } from '../hooks/useReveal.js';
import { Figure } from '../components/ui/Figure.jsx';
import { MEDIA } from '../data/media.js';
import styles from './Programs.module.css';

const PROGRAMS = [
  {
    icon: Home,
    img: MEDIA.field,
    title: 'Shelter & housing',
    body: 'Repairs to leaking roofs, safe walls and basic sanitation for families living in unsafe structures — rebuilt before the monsoon, not after.',
    outcome: 'Weatherproof homes',
  },
  {
    icon: Wheat,
    img: MEDIA.market,
    title: 'Ration & nutrition',
    body: 'Monthly dry-ration kits and emergency food support, paid vendor-direct to a local shop so the value reaches the kitchen, not a middleman.',
    outcome: 'Food on the table',
  },
  {
    icon: HeartPulse,
    img: MEDIA.medical,
    title: 'Medical & emergency aid',
    body: 'Hospital bills, medicines, surgeries and ambulance costs settled directly with the provider for families facing a sudden health crisis.',
    outcome: 'Treatment, on time',
  },
  {
    icon: GraduationCap,
    img: MEDIA.children,
    title: 'Education support',
    body: 'School fees, books, uniforms and exam costs so a child stays in class through a hard year instead of dropping out to work.',
    outcome: 'Children in school',
  },
  {
    icon: Droplets,
    img: MEDIA.water,
    title: 'Water & sanitation',
    body: 'Clean-water access and sanitation fixes for households and the shared facilities that a whole street depends on.',
    outcome: 'Safe, clean water',
  },
  {
    icon: LifeBuoy,
    img: MEDIA.volunteers,
    title: 'Livelihood & dignity',
    body: 'One-time grants for tools, a sewing machine or a cart — the small capital that turns a daily wage into a standing livelihood.',
    outcome: 'A way to earn',
  },
];

export function Programs() {
  const ref = useReveal({ threshold: 0.1 });
  return (
    <section id="programs" className={styles.section}>
      <div className="container">
        <div className={styles.head}>
          <div>
            <p className="eyebrow">What we do</p>
            <h2 className={styles.title}>Six ways help reaches the ground.</h2>
          </div>
          <p className={styles.intro}>
            Every programme runs through the same proof chain — verified intake, ground
            verification, tiered approval and a delivery acknowledgement. The category changes;
            the standard of evidence never does.
          </p>
        </div>

        <div ref={ref} className={styles.grid} data-revealed="false">
          {PROGRAMS.map((p, i) => (
            <article key={p.title} className={styles.card} style={{ '--i': i }}>
              <div className={styles.media}>
                <Figure id={p.img} alt={p.title} ratio="16 / 10" w={640} />
                <span className={styles.iconBadge}>
                  <p.icon size={20} aria-hidden="true" />
                </span>
              </div>
              <div className={styles.cardBody}>
                <h3 className={styles.cardTitle}>{p.title}</h3>
                <p className={styles.cardCopy}>{p.body}</p>
                <span className={styles.outcome}>{p.outcome}</span>
              </div>
            </article>
          ))}
        </div>

        <div className={styles.foot}>
          <Link to="/served" className={styles.footLink}>
            See real cases on the Served wall <ArrowRight size={18} aria-hidden="true" />
          </Link>
        </div>
      </div>
    </section>
  );
}

export default Programs;
